import { App, theme, Tooltip } from 'antd'

import { StatusTag } from '@/Ship/Components/StatusTag'
import { classNames } from '@/Ship/Helpers/classNames'
import { copyToClipboard } from '@/Ship/Helpers/copyToClipboard'

import type { CSSProperties, ReactNode } from 'react'

import styles from './EntityCard.module.css'
import { useEntityCardPalette } from './EntityLayout'

export interface EntityHeaderProps {
    /** Номер документа чи позиції: «№ 143», «or-000012» */
    code?: ReactNode
    /** Статус — тим самим бейджем, що в колонці реєстру */
    status?: { label: string; color?: string | undefined } | undefined
    /** Додаткові позначки поруч зі статусом: пріоритет, напрям, вид */
    badges?: ReactNode
    /** Показники праворуч у рядку метаданих: дедлайн, сума, дата */
    aside?: ReactNode
    /** Заголовок сутності — у перегляді; у формі його вводять полем */
    title?: ReactNode
    /** Рядок під заголовком: контрагент, автор, коротке пояснення */
    subtitle?: ReactNode
}

/**
 * Шапка картки сутності — те, що читають першим.
 *
 * Без неї ідентифікатор, статус і назва були б звичайними полями серед решти:
 * назва справи стояла б під підписом «НАЗВА» в тому ж ряду, що «ОПИС», а
 * статус клієнта — десятим полем у бічній панелі. Читач картки шукає спершу «що це і в
 * якому стані», тож ці три речі стоять зверху, а назва — заголовком.
 *
 * Компонент спільний для всіх карток: однакова шапка робить різні сутності
 * впізнаваними як один застосунок. Білий блок навколо дає `EntityTabs` (разом
 * зі смугою вкладок) або `EntityHero`.
 */
export const EntityHeader = ({ code, status, badges, aside, title, subtitle }: EntityHeaderProps) => {
    const palette = useEntityCardPalette()

    const hasMeta = code !== undefined || status !== undefined || badges !== undefined || aside !== undefined

    if (!hasMeta && title === undefined && subtitle === undefined) return null

    return (
        <div className={classNames(styles['header'])} style={palette}>
            {hasMeta ? (
                <div className={classNames(styles['meta'])}>
                    {code !== undefined ? <span className={classNames(styles['code'])}>{code}</span> : null}
                    {status ? <StatusTag label={status.label} color={status.color} inline /> : null}
                    {badges}
                    {aside !== undefined ? <span className={classNames(styles['aside'])}>{aside}</span> : null}
                </div>
            ) : null}

            {title !== undefined ? <h2 className={classNames(styles['title'])}>{title}</h2> : null}
            {subtitle !== undefined ? <div className={classNames(styles['subtitle'])}>{subtitle}</div> : null}
        </div>
    )
}

export interface HeaderChipProps {
    icon?: ReactNode
    children: ReactNode
    /** Тон: нейтральний, попередження (сьогодні), помилка (прострочено), успіх */
    tone?: 'neutral' | 'warning' | 'error' | 'success'
    title?: string
    /**
     * Клік копіює цей текст. Для контактів у шапці: телефон і пошту частіше
     * копіюють у поштовий сервіс чи месенджер, ніж відкривають типовою
     * програмою системи, — тож `tel:` і `mailto:` тут програють копіюванню.
     */
    copyText?: string
}

/** Показник у рядку метаданих шапки: дата, сума, кількість, контакт. */
export const HeaderChip = ({ icon, children, tone = 'neutral', title, copyText }: HeaderChipProps) => {
    const { token } = theme.useToken()
    const { message } = App.useApp()

    const tones = {
        neutral: { bg: token.colorFillSecondary, text: token.colorText },
        warning: { bg: token.colorWarningBg, text: token.colorWarningText },
        error: { bg: token.colorErrorBg, text: token.colorErrorText },
        success: { bg: token.colorSuccessBg, text: token.colorSuccessText },
    } as const

    const style = {
        '--ec-chip-bg': tones[tone].bg,
        '--ec-chip-text': tones[tone].text,
        '--ec-chip-hover': token.colorFill,
    } as CSSProperties

    // Без окремої іконки копіювання: у бейджі вона зайва — про дію кажуть
    // реакція на наведення й підказка
    const content = (
        <>
            {icon ? <span className={classNames(styles['chipIcon'])}>{icon}</span> : null}
            {children}
        </>
    )

    if (copyText === undefined) {
        return (
            <span className={classNames(styles['chip'])} style={style} {...(title ? { title } : {})}>
                {content}
            </span>
        )
    }

    return (
        <Tooltip title="Натисніть, щоб скопіювати">
            <button
                type="button"
                className={classNames(styles['chip'], styles['chipButton'])}
                style={style}
                aria-label={`Скопіювати: ${copyText}`}
                onClick={() => {
                    void copyToClipboard(copyText).then((ok) => {
                        if (ok) void message.success(`Скопійовано: ${copyText}`)
                        else void message.error('Не вдалося скопіювати')
                    })
                }}
            >
                {content}
            </button>
        </Tooltip>
    )
}
